/**
 * M4 «Direzione» — il modulo del presidente.
 *
 * Cinque sezioni, che sono **il flusso dell'impianto** letto da valle a monte come
 * lo guarda Enzo: il Cruscotto (la giornata intera) e poi Uscite ·
 * Confezionamento · Produzione · Materie prime.
 *
 * Fino al 18-08-2026 M4 non aveva una pagina: era un insieme di widget dentro la
 * dashboard, e `modulo.M4` era l'unica chiave di modulo che non governava niente.
 *
 * ⚠️ **Il modulo non scrive niente.** Non c'è un solo pulsante che cambi qualcosa,
 * ed è una scelta di prodotto: al presidente si dà controllo, non compiti
 * operativi. Se un giorno servisse un'azione, va prima ridiscussa.
 */

import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";

import { PageContainer } from "@/components/PageContainer";
import { RigaTesta } from "@/components/RigaTesta";
import { TabModulo } from "@/components/TabModulo";
import { SEZIONI_M4 } from "@/lib/nav-config";
import { CruscottoM4 } from "@/components/m4/CruscottoM4";
import {
  ConfezionamentoM4,
  MateriePrimeM4,
  ProduzioneM4,
  UsciteM4,
} from "@/components/m4/sezioni";
import {
  useM4Confezionamento,
  useM4Giornata,
  useM4MateriePrime,
  useM4Produzione,
  useM4Settimana,
  useM4Uscite,
} from "@/hooks/useM4";

type Tab = "cruscotto" | "uscite" | "confezionamento" | "produzione" | "materie";

// L'elenco delle sezioni non sta più qui: è `SEZIONI_M4` in `nav-config.ts`,
// lo stesso che disegna la barra laterale. Erano due liste da tenere allineate a
// mano, ed è così che Spedizioni era finita con cinque voci in barra e tre tab.
const CHIAVI: Tab[] = ["cruscotto", "uscite", "confezionamento", "produzione", "materie"];

export function M4DirezionePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = CHIAVI.includes(tabParam as Tab) ? (tabParam as Tab) : "cruscotto";
  // Il giorno vive nell'URL: così un indirizzo è condivisibile e il tasto
  // «indietro» torna al giorno di prima invece che a un altro modulo.
  const giornoUrl = searchParams.get("giorno") ?? undefined;

  function scegliGiorno(giorno: string) {
    const p = new URLSearchParams(searchParams);
    p.set("giorno", giorno);
    setSearchParams(p, { replace: true });
  }

  return (
    <PageContainer className="space-y-5">
      <TabModulo sezioni={SEZIONI_M4} modulo="M4" />

      {tab === "cruscotto" && <Cruscotto giorno={giornoUrl} onGiorno={scegliGiorno} />}
      {tab === "uscite" && <Uscite />}
      {tab === "confezionamento" && <Confezionamento />}
      {tab === "produzione" && <Produzione />}
      {tab === "materie" && <MateriePrime />}
    </PageContainer>
  );
}

function Caricamento({ cosa }: { cosa: string }) {
  return (
    <p className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> {cosa}
    </p>
  );
}

function Errore({ cosa }: { cosa: string }) {
  return (
    <p className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-3 text-sm text-destructive">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      Non è stato possibile leggere {cosa}. Riprova, o segnalalo se continua.
    </p>
  );
}

function Cruscotto({
  giorno,
  onGiorno,
}: {
  giorno?: string;
  onGiorno: (g: string) => void;
}) {
  const giornata = useM4Giornata(giorno);
  const settimana = useM4Settimana(giorno);

  if (giornata.isLoading || settimana.isLoading) return <Caricamento cosa="la giornata" />;
  if (giornata.isError || !giornata.data || settimana.isError || !settimana.data)
    return <Errore cosa="la giornata dell'impianto" />;

  const g = giornata.data;
  const conLavoro = g.giorni_con_lavorazioni;
  // Il selettore propone solo giornate che hanno lavorazioni, più quella scelta:
  // offrire giorni vuoti sarebbe offrire pagine vuote.
  const proposte = [...new Set([g.giorno, ...conLavoro])].sort();
  const indice = proposte.indexOf(g.giorno);

  return (
    <div className="space-y-4">
      <RigaTesta
        contesto={
          g.totali.lavorazioni === 0
            ? "nessuna lavorazione in piano"
            : `${g.totali.lavorazioni} lavorazioni`
        }
        caricatoIl={g.piano_importato_il}
        etichettaData="piano del"
        titoloBadge="Il piano viene da un caricamento manuale: più invecchia, meno descrive oggi"
        destra={
          g.orizzonte_da && g.orizzonte_a
            ? `orizzonte del piano: ${g.orizzonte_da} → ${g.orizzonte_a}`
            : undefined
        }
      />

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.025em]">
            {formattaGiorno(g.giorno)}
          </h1>
          <p className="mt-1 max-w-[62ch] text-xs text-muted-foreground">
            {g.totali.lavorazioni === 0
              ? "L'impianto è fermo: il piano non ha nessuna lavorazione in questa giornata."
              : `Il piano prevede ${g.totali.lavorazioni} lavorazioni. È il previsto, non il fatto: il MES non ci manda l'avanzamento.`}
          </p>
        </div>
        {proposte.length > 1 && (
          <div className="flex items-center gap-1 rounded-lg bg-muted p-0.5">
            <button
              type="button"
              disabled={indice <= 0}
              onClick={() => onGiorno(proposte[indice - 1])}
              className="rounded-md px-2.5 py-1 text-xs text-muted-foreground disabled:opacity-40 enabled:hover:bg-card enabled:hover:text-foreground"
            >
              ‹ prima
            </button>
            <select
              value={g.giorno}
              onChange={(e) => onGiorno(e.target.value)}
              className="rounded-md bg-card px-2 py-1 text-xs font-semibold shadow-sm"
              aria-label="Giornata"
            >
              {proposte.map((d) => (
                <option key={d} value={d}>
                  {formattaGiorno(d)}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={indice < 0 || indice >= proposte.length - 1}
              onClick={() => onGiorno(proposte[indice + 1])}
              className="rounded-md px-2.5 py-1 text-xs text-muted-foreground disabled:opacity-40 enabled:hover:bg-card enabled:hover:text-foreground"
            >
              dopo ›
            </button>
          </div>
        )}
      </div>

      <CruscottoM4 giornata={g} settimana={settimana.data} />
    </div>
  );
}

function formattaGiorno(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const testo = d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  // I mesi in italiano sono minuscoli: si alza solo l'iniziale della frase.
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}

function Uscite() {
  const q = useM4Uscite();
  if (q.isLoading) return <Caricamento cosa="le uscite" />;
  if (q.isError || !q.data) return <Errore cosa="le uscite" />;
  return <UsciteM4 dati={q.data} />;
}

function Confezionamento() {
  const q = useM4Confezionamento();
  if (q.isLoading) return <Caricamento cosa="il confezionamento" />;
  if (q.isError || !q.data) return <Errore cosa="il confezionamento" />;
  return <ConfezionamentoM4 dati={q.data} />;
}

function Produzione() {
  const q = useM4Produzione();
  if (q.isLoading) return <Caricamento cosa="la produzione" />;
  if (q.isError || !q.data) return <Errore cosa="la produzione" />;
  return <ProduzioneM4 dati={q.data} />;
}

function MateriePrime() {
  const q = useM4MateriePrime();
  if (q.isLoading) return <Caricamento cosa="le materie prime" />;
  if (q.isError || !q.data) return <Errore cosa="le materie prime" />;
  return <MateriePrimeM4 dati={q.data} />;
}
